import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Vessel } from '../../dto/vessel';
import { FileService } from '../../../../common/file/service/file.service';
import { Subject, takeUntil } from 'rxjs';
import { VesselPhotoService } from '../../service/vessel-photo.service';
import { VesselPhoto } from '../../dto/vessel-photo';

@Component({
  selector: 'dpi-vessel-photos',
  imports: [MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule, MatPaginatorModule, MatTooltipModule, NgIf],
  templateUrl: './vessel-photos.component.html',
  styleUrl: './vessel-photos.component.scss'
})
export class VesselPhotosComponent implements OnInit, OnDestroy {
  vessel: Vessel;
  listPhoto: VesselPhoto[] = [];
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  imgFromApi: string = '';
  destroyed$ = new Subject<void>(); // notifier of component destroyed

  pageLength = 0;
  pageSize = 1;
  pageIndex = 0;
  pageSizeOptions = [1];
  hidePageSize = true;
  showPageSizeOptions = false;
  showFirstLastButtons = true;
  disabled = false;
  pageEvent: PageEvent | undefined;
  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.pageLength = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.reloadImages(this.vessel.id);
  }

  // 配置项
  allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']; // 允许的文件类型
  maxSizeMB = 100; // 最大 100MB

  // 状态
  selectedFile: File | null = null;
  isDragging = false;
  uploadError = '';

  constructor(private notifyService: StateNotifyService, private vesselPhotoService: VesselPhotoService, private fileService: FileService) {
    this.vessel = notifyService.getSelectedVessel();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.vessel = model;
        this.loadListPhoto(model.id, true);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
        this.resetStatus();
      });
  }

  loadListPhoto(vesselId: number, displayFromFirstOne: boolean) {
    this.vesselPhotoService.getAllImagesOfVessel(vesselId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.listPhoto = res;
          this.listSize.emit(this.listPhoto.length);
          if (displayFromFirstOne) {
            this.pageIndex = 0;
          } else {
            this.pageIndex = this.listPhoto.length - 1;
          }
          this.reloadImages(vesselId);
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.listPhoto = [];
        }
      });
  }

  reloadImages(vesselId: number) {
    this.pageLength = this.listPhoto.length;
    if (this.pageLength > 0) {
      const photoRecord = this.listPhoto[this.pageIndex];
      this.vesselPhotoService.getVesselImage(photoRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (img: any) => {
            this.imgFromApi = this.fileService.arrayBufferToBase64(img);
            console.log('Image: ' + photoRecord);
          },
          error: (error: any) => {
            console.error('Error: ', error);
            this.imgFromApi = '';
          }
        });
    } else {
      this.imgFromApi = '';
    }
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.vesselPhotoService.fileUpload(this.vessel.id, file).subscribe({
        next: res => {
          this.loadListPhoto(this.vessel.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }
  }

  deletePhoto() {
    if (this.pageLength > 0) {
      const diagramRecord = this.listPhoto[this.pageIndex];
      this.vesselPhotoService.deleteVesselPhoto(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (res: any) => {
            this.loadListPhoto(this.vessel.id, true);
          },
          error: (error: any) => {
            console.error('Error: ', error);
          }
        });
    }
  }

  // ------------------------------
  // 拖放事件
  // ------------------------------
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave() {
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndSetFile(files[0]);
    }
  }

  // ------------------------------
  // 文件验证与上传
  // ------------------------------
  validateAndSetFile(file: File) {
    this.resetStatus();

    // 检查类型
    if (!this.allowedTypes.includes(file.type)) {
      this.uploadError = '不支持此文件类型';
      return;
    }

    // 检查大小
    const maxSize = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = `文件不能超过 ${this.maxSizeMB}MB`;
      return;
    }

    this.selectedFile = file;

    if (file) {
      this.vesselPhotoService.fileUpload(this.vessel.id, file).subscribe({
        next: res => {
          this.loadListPhoto(this.vessel.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }

  }

  // 重置
  resetStatus() {
    this.selectedFile = null;
    this.uploadError = '';
  }

}
